// Events module types (mirrors podcast/LMS patterns)

export interface EventSpeaker {
  id: string;
  brand_id: string;
  name: string;
  slug?: string | null;
  title?: string | null;
  bio?: string | null;
  email?: string | null;
  image_url?: string | null;
  social_links?: Record<string, string> | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EventMaterial {
  type: "video" | "pdf" | "image" | "link";
  title: string;
  url: string;
}

export interface Event {
  id: string;
  brand_id: string;
  user_id?: string | null;
  speaker_id?: string | null;
  co_speaker_id?: string | null;
  title: string;
  topic?: string | null;
  description?: string | null;
  slug?: string | null;
  thumbnail?: string | null;
  image_url?: string | null;
  inner_page_image?: string | null;
  target_record_date?: string | null;
  target_publish_date?: string | null;
  actual_record_date?: string | null;
  actual_publish_date?: string | null;
  status: string;
  notes?: string | null;
  recording_link?: string | null;
  invitation_recording_link?: string | null;
  published_links?: Record<string, string>;
  what_you_will_learn?: string | null;
  story_behind?: string | null;
  questions?: string | null;
  visible: boolean;
  address?: string | null;
  time?: string | null;
  duration?: number | null;
  speaking_language?: string | null;
  material_language?: string | null;
  speaking_languages?: { code: string; name: string }[] | null;
  material_languages?: { code: string; name: string }[] | null;
  for_whom?: string | null;
  long_description?: string | null;
  benefits?: string | null;
  goals?: string | null;
  visual_1?: string | null;
  visual_2?: string | null;
  syllabus?: string | null;
  testimonial_ids?: string[] | null;
  client_logo_ids?: string[] | null;
  product_id?: string | null;
  materials?: EventMaterial[] | null;
  // DigComp 3.0 fields
  digcomp_competence_id?: string | null;
  digcomp_area_id?: string | null;
  digcomp_level_id?: string | null;
  // Eventbrite
  eventbrite_event_id?: string | null;
  eventbrite_synced_at?: string | null;
  // Certificate template for attendance certificates
  certificate_template_id?: string | null;
  created_at: string;
  updated_at: string;
  speaker?: EventSpeaker | null;
  co_speaker?: EventSpeaker | null;
}

export interface EventLead {
  id: string;
  brand_id: string;
  event_id: string;
  type: 'waiting_list' | 'pre_registration' | 'material_request' | 'registration' | 'purchase' | 'engagement';
  email: string;
  name?: string | null;
  phone?: string | null;
  notes?: string | null;
  metadata?: Record<string, any> | null;
  status: string;
  created_at: string;
  updated_at: string;
  event?: { id: string; title: string } | null;
}

export interface EventCertificate {
  id: string;
  event_id: string;
  lead_id: string;
  certificate_url: string;
  issued_at: string;
  verification_code: string;
}

export interface EventCampaign {
  id: string;
  brand_id: string;
  user_id: string;
  event_id?: string | null;
  name: string;
  description?: string | null;
  campaign_type: string;
  status: string;
  created_at: string;
  updated_at: string;
  event?: Event | null;
  emails?: EventEmail[];
}

export interface EventEmail {
  id: string;
  campaign_id: string;
  sequence_order: number;
  subject: string;
  content?: string | null;
  delay_days: number;
  delay_hours: number;
  send_conditions: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface EventSend {
  id: string;
  campaign_id: string;
  email_id: string;
  lead_id: string;
  event_id?: string | null;
  recipient_email: string;
  status: string;
  scheduled_at?: string | null;
  sent_at?: string | null;
  opened_at?: string | null;
  replied_at?: string | null;
  error_message?: string | null;
  esp_message_id?: string | null;
  created_at: string;
  updated_at: string;
  lead?: EventLead;
  event?: Event;
}

export interface EventPrompt {
  id: string;
  brand_id: string;
  prompt_type: string;
  name: string;
  prompt: string;
  created_at: string;
  updated_at: string;
}

export interface EventAssociation {
  id: string;
  brand_id: string;
  name: string;
  description?: string | null;
  logo_url?: string | null;
  academy_ids?: string[];
  payment_type?: 'commission' | 'static' | 'volume';
  commission_percentage?: number;
  static_payment?: number;
  payment_currency?: string;
  xero_contact_id?: string | null;
  email?: string | null;
  phone?: string | null;
  address_street?: string | null;
  address_city?: string | null;
  address_region?: string | null;
  address_postal_code?: string | null;
  address_country?: string | null;
  tax_number?: string | null;
  company_number?: string | null;
  bank_account_details?: string | null;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
}

export interface EventAssociationType {
  id: string;
  name: string;
  label: string;
  created_at?: string;
}

export interface EventAssociationLink {
  id: string;
  event_id: string;
  association_id: string;
  association_type_id: string;
  created_at?: string;
  association?: EventAssociation;
  association_type?: EventAssociationType;
}

export type EventSpeakerInsert = Omit<EventSpeaker, 'id' | 'created_at' | 'updated_at'>;
export type EventInsert = Omit<Event, 'id' | 'created_at' | 'updated_at' | 'speaker' | 'co_speaker'>;
export type EventLeadInsert = Omit<EventLead, 'id' | 'created_at' | 'updated_at' | 'event'>;
export type EventCampaignInsert = Omit<EventCampaign, 'id' | 'created_at' | 'updated_at' | 'event' | 'emails'>;
export type EventEmailInsert = Omit<EventEmail, 'id' | 'created_at' | 'updated_at'>;
export type EventPromptInsert = Omit<EventPrompt, 'id' | 'created_at' | 'updated_at'>;
export type EventAssociationInsert = Omit<EventAssociation, 'id' | 'created_at' | 'updated_at'>;

export const EVENT_STATUS_OPTIONS = [
  { value: 'planned', label: 'Planned' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'recorded', label: 'Recorded' },
  { value: 'editing', label: 'Editing' },
  { value: 'published', label: 'Published' },
] as const;

export const EVENT_LEAD_TYPE_OPTIONS = [
  { value: 'waiting_list', label: 'Waiting list' },
  { value: 'pre_registration', label: 'Pre-registration' },
  { value: 'material_request', label: 'Material request' },
  { value: 'registration', label: 'Registration' },
  { value: 'purchase', label: 'Purchase' },
  { value: 'engagement', label: 'Engagement' },
] as const;

export const EVENT_LEAD_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'converted', label: 'Converted' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

export const EVENT_CAMPAIGN_TYPE_OPTIONS = [
  { value: 'invitation', label: 'Invitation' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'pre_event', label: 'Pre-Event' },
  { value: 'post_event', label: 'Post-Event' },
] as const;

// Events URL settings (site_url + url_prefixes for list/single links)
export interface EventsUrlPrefixes {
  events?: string;
  event?: string;
}

export interface EventsSettings {
  id: string;
  brand_id: string;
  user_id?: string | null;
  site_url: string | null;
  url_prefixes: EventsUrlPrefixes | null;
  eventbrite_oauth_token?: string | null;
  eventbrite_organization_id?: string | null;
  created_at: string;
  updated_at: string;
}
